import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { getProductByIds } from 'apis/offer-listing-service/api';
import { Product } from 'apis/offer-listing-service/types';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';

type SelectionProductsData = {
  selectionProducts: Product[];
  loadingSelectionProducts: boolean;
  refetchSelectionProducts: () => void;
  updateSelectionProductsCache: (...newProduct: Product[]) => void;
};

const buildQueryKey = (scopeId: string) => ['selection-products', scopeId];

export const useSelectionProducts = (productIds: string[] = []): SelectionProductsData => {
  const queryClient = useQueryClient();
  const scope = useScopeContext();
  const queryKey = useMemo(() => buildQueryKey(scope.id), [scope.id]);
  const {
    data = [],
    isFetching,
    refetch,
  } = useQuery(queryKey, () => getProductByIds(productIds), {
    enabled: productIds.length > 0,
    staleTime: Infinity,
    retry: false,
    select: data => data.filter(product => !!product) || [],
  });

  const updateSelectionProductsCache = useCallback(
    (...newProducts: Product[]) =>
      queryClient.setQueriesData(queryKey, (oldData: Product[] = []) => [
        ...oldData.filter(product => !newProducts.find(np => np.id === product.id)),
        ...newProducts,
      ]),
    [queryClient, queryKey],
  );

  return {
    selectionProducts: data,
    loadingSelectionProducts: isFetching,
    refetchSelectionProducts: refetch,
    updateSelectionProductsCache,
  };
};
